import type {
  FAQ,
  Recipe,
} from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import { mergeDeep, mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';
import { Accordion } from '@components/Accordion';
import { Aside } from '@components/Aside/Aside';
import { SocialLinks } from '@components/Social';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { log, logBreadcrumb } from '@lib/logs';
import {
  PortableText,
  type PortableTextMarkComponentProps,
  type PortableTextTypeComponentProps,
} from '@portabletext/react';
import type { PortableTextBlock } from '@portabletext/types';
import type { CSSProperties, ReactElement } from 'react';
import { type ReactNode, useEffect, useState } from 'react';
import { CarouselRenderer } from './Carousel';
import { CustomFormRenderer, ExternalFormRenderer } from './Form';
import { ImageRenderer } from './Image';
import { ImageSetRenderer } from './ImageSet';
import {
  InternalLinkRenderer,
  InternalLinkRendererDarkBg,
  LinkRenderer,
  LinkRendererDarkBg,
  OnPageLinkRenderer,
} from './Link';
import { BulletListRenderer, NumberedListRenderer } from './List';
import { NutritionPanelRenderer } from './NutritionPanel';
import { RecipeRenderer } from './Recipe';
import { RecommendedProductRenderer } from './RecommendedProduct';
import { TableRenderer } from './Table';
import {
  MuxPortableTextRenderer,
  VimeoPortableTextRenderer,
} from './VideoRenderers';

function AsideRenderer(
  props: PortableTextTypeComponentProps<{
    _type: string;
    content: PortableTextBlock[];
  }>
): ReactElement {
  return (
    <Aside>
      <PortableTextBlockRenderer content={props.value.content} />
    </Aside>
  );
}
function MessageAsideRenderer(
  props: PortableTextTypeComponentProps<{
    _type: string;
    content: PortableTextBlock[];
  }>
): ReactElement {
  return (
    <aside className="clear-both mx-0 my-7 rounded-2xl border border-solid border-orange bg-orange-20 px-5 py-2.5 text-grey-dark dark:bg-orange-dark dark:text-white">
      <PortableTextBlockRenderer content={props.value.content} />
    </aside>
  );
}
function WarningAsideRenderer(
  props: PortableTextTypeComponentProps<{
    _type: string;
    content: PortableTextBlock[];
  }>
): ReactElement {
  return (
    <aside className="clear-both mx-0 my-7 border border-solid border-white rounded-2xl bg-grey-dark px-5 py-2.5 text-grey-light shadow-[0_0_2px_1px_var(--color-orange)] dark:bg-grey-darkest">
      <PortableTextBlockRenderer content={props.value.content} />
    </aside>
  );
}

function ColumnsRenderer(
  props: PortableTextTypeComponentProps<{
    _type: string;
    colNumber: number;
    content: {
      contentBody: PortableTextBlock[];
    }[];
  }>
): ReactElement {
  return (
    <div
      style={{ '--desired-columns': props.value.colNumber } as CSSProperties}
      className="
        grid grid-cols-[repeat(var(--columns),1fr)] [--columns:1] 
        sm:[--columns:max(2,calc(--desired-columns-1))] md:[--columns:var(--desired-columns)]
      "
    >
      {props.value.content.map((content, idx) => (
        <div key={idx}>
          <PortableTextBlockRenderer content={content.contentBody} />
        </div>
      ))}
    </div>
  );
}

const FaqRenderer = (
  props: PortableTextTypeComponentProps<FAQ>
): ReactElement => {
  const [isOpen, setIsOpen] = useState(!props.value.collapsedByDefault);

  // Reset open state when question changes
  useEffect(() => {
    setIsOpen(!props.value.collapsedByDefault);
  }, [props.value.question]);

  return (
    <Accordion
      key={`faq-${props.value.question}`}
      defaultOpen={!props.value.collapsedByDefault}
      items={[
        {
          title: props.value.question,
          content: <PortableTextBlockRenderer content={props.value.answer} />,
          id: props.value.question.split(' ').join('-'),
        },
      ]}
    />
  );
};

const SubscriptRenderer = (props: PortableTextMarkComponentProps) => (
  <sub>{props.children}</sub>
);

const SuperscriptRenderer = (props: PortableTextMarkComponentProps) => (
  <sup>{props.children}</sup>
);

const CenteredRenderer = (props: PortableTextMarkComponentProps) => (
  <span className="block text-center">{props.children}</span>
);

const CurrentYearRenderer = (props: PortableTextMarkComponentProps) => (
  <>{new Date().getFullYear()}</>
);

const StandoutTextRenderer = (props: PortableTextMarkComponentProps) => (
  <span className="text-orange block">{props.children?.toString().trim()}</span>
);

function SocialLinksRenderer(
  props: PortableTextTypeComponentProps<{
    _type: string;
    name: string;
    noFollow: boolean;
    facebook?: string;
    instagram?: string;
    twitter?: string;
    tiktok?: string;
    linkedIn?: string;
    website?: string;
    youTube?: string;
  }>
): ReactElement {
  return (
    <SocialLinks
      name={props.value.name}
      noFollow={props.value.noFollow}
      facebook={props.value.facebook}
      instagram={props.value.instagram}
      twitter={props.value.twitter}
      tiktok={props.value.tiktok}
      linkedIn={props.value.linkedIn}
      website={props.value.website}
      youTube={props.value.youTube}
    />
  );
}

export const DarkBackgroundOverrides = {
  marks: {
    link: LinkRendererDarkBg,
    internalLink: InternalLinkRendererDarkBg,
  },
};

const myComponents = {
  block: {
    normal: ({ children }: { children?: ReactNode }) => (
      <Paragraph>{children}</Paragraph>
    ),
    blockquote: ({ children }: { children?: ReactNode }) => (
      <blockquote className="text-lg font-primary italic px-8 my-4 max-w-[65ch]">
        {children}
      </blockquote>
    ),
    h2: ({ children }: { children?: ReactNode }) => <h2>{children}</h2>,
    a: ({ children }) => <>{children}</>,
  },
  list: {
    bullet: BulletListRenderer,
    number: NumberedListRenderer,
  },
  types: {
    faq: FaqRenderer,
    'cloudinary.asset': ImageRenderer,
    image: ImageRenderer,
    imageSet: ImageSetRenderer,
    nutritionPanel: NutritionPanelRenderer,
    form: ExternalFormRenderer,
    customForm: CustomFormRenderer,
    columns: ColumnsRenderer,
    vimeoVideo: VimeoPortableTextRenderer,
    muxVideo: MuxPortableTextRenderer,
    recipe: RecipeRenderer,
    aside: AsideRenderer,
    insertAside: AsideRenderer,
    table: TableRenderer,
    message: MessageAsideRenderer,
    warning: WarningAsideRenderer,
    recommendedProduct: RecommendedProductRenderer,
    socialLinks: SocialLinksRenderer,
    carousel: CarouselRenderer,
  },
  marks: {
    link: LinkRenderer,
    internalLink: InternalLinkRenderer,
    onPageLink: OnPageLinkRenderer,
    blogPost: InternalLinkRenderer,
    category: InternalLinkRenderer,
    ambassador: InternalLinkRenderer,
    page: InternalLinkRenderer,
    product: InternalLinkRenderer,
    sub: SubscriptRenderer,
    sup: SuperscriptRenderer,
    center: CenteredRenderer,
    currentYear: CurrentYearRenderer,
    standoutText: StandoutTextRenderer,
  },
};

// These are the ones that do a LinkRenderer
const LINK_MARK_TYPES = new Set([
  'link',
  'internalLink',
  'blogPost',
  'category',
  'ambassador',
  'page',
  'product',
]);

function isPortableTextBlockNode(value: unknown): value is PortableTextBlock {
  return !!value && typeof value === 'object' && '_type' in value;
}

const sanitizeBlock = (block: PortableTextBlock): PortableTextBlock => {
  // Only text blocks with mark definitions and children can contain link annotations.
  if (!Array.isArray(block.markDefs) || !Array.isArray(block.children)) {
    return block;
  }

  const markDefs = block.markDefs;
  // Build a set of mark keys that correspond to link-like annotations.
  const linkMarkKeys = new Set(
    markDefs
      .filter(
        (def) => def?._key && def?._type && LINK_MARK_TYPES.has(def._type)
      )
      .map((def) => def._key as string)
  );

  // No link marks found, return the block as is
  if (linkMarkKeys.size === 0) {
    return block;
  }

  let blockChanged = false;
  const children = block.children.map((child) => {
    // Nothing to sanitize when there are fewer than two marks.
    if (!Array.isArray(child?.marks) || child.marks.length < 2) {
      return child;
    }

    const nextMarks: string[] = [];
    let hasLinkMark = false;

    for (const mark of child.marks) {
      // Keep only the first link mark, but preserve all non-link marks.
      if (linkMarkKeys.has(mark)) {
        if (hasLinkMark) {
          blockChanged = true;
          continue;
        }
        hasLinkMark = true;
      }

      nextMarks.push(mark);
    }

    if (nextMarks.length === child.marks.length) {
      return child;
    }

    return {
      ...child,
      marks: nextMarks,
    };
  });

  if (!blockChanged) {
    return block;
  }

  return {
    ...block,
    children,
  };
};

function sanitizeNestedLinkMarks(
  content: ContentTypes | ContentTypes[]
): ContentTypes | ContentTypes[] {
  if (Array.isArray(content)) {
    return content.map((item) =>
      isPortableTextBlockNode(item)
        ? sanitizeBlock(item as PortableTextBlock)
        : item
    );
  }

  return isPortableTextBlockNode(content)
    ? sanitizeBlock(content as PortableTextBlock)
    : content;
}

export type ContentTypes = PortableTextBlock | FAQ | Recipe;

interface Props {
  content: ContentTypes | ContentTypes[];
  componentsOverride?: any;
  customClass?: string;
}

export function PortableTextBlockRenderer({
  content,
  componentsOverride,
  customClass,
}: Props) {
  if (!content) return null;

  const sanitizedContent = sanitizeNestedLinkMarks(content);

  let hasFigure = false;
  try {
    hasFigure = Array.isArray(content)
      ? !!content.find(
          (b) => b._type === 'image' || b._type === 'cloudinary.asset'
        )
      : content._type === 'image' || content._type === 'cloudinary.asset';
  } catch (err) {
    console.log(content);
  }

  let hasCarousel = false;
  try {
    hasCarousel = Array.isArray(content)
      ? !!content.find((b) => b._type === 'carousel')
      : content._type === 'carousel';
  } catch (err) {
    console.log(content);
  }

  return (hasFigure || customClass) && !hasCarousel ? (
    <div className={mergeStyles(customClass, 'has-[>figure]:clearfix')}>
      <PortableText
        value={sanitizedContent}
        components={
          componentsOverride !== undefined
            ? mergeDeep({}, myComponents, componentsOverride)
            : myComponents
        }
        onMissingComponent={(message, options) => {
          logBreadcrumb({
            category: 'portableText',
            message: 'Unknown portable text renderer',
            level: 'error',
            data: {
              type: options.type,
              nodeType: options.nodeType,
              message,
            },
          });
          log({
            error: 'Unknown portable text type',
            location: 'PortableText',
          });
        }}
      />
    </div>
  ) : (
    <PortableText
      value={sanitizedContent}
      components={
        componentsOverride !== undefined
          ? mergeDeep({}, myComponents, componentsOverride)
          : myComponents
      }
      onMissingComponent={(message, options) => {
        logBreadcrumb({
          category: 'portableText',
          message: 'Unknown portable text renderer',
          level: 'error',
          data: {
            type: options.type,
            nodeType: options.nodeType,
            message,
          },
        });
        log({ error: 'Unknown portable text type', location: 'PortableText' });
      }}
    />
  );
}
